import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Box,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  PlayArrow as PlayIcon,
  Edit as EditIcon,
  Science as ScienceIcon,
  LocalShipping as ShippingIcon,
  Inventory as InventoryIcon,
  Factory as FactoryIcon,
  TrendingUp as TrendingUpIcon,
  Warehouse as WarehouseIcon,
} from '@mui/icons-material';

interface SimulationTemplate {
  id: string;
  name: string;
  description: string;
  type: string;
  scenario_count: number;
}

interface TemplateDetail {
  template_id: string;
  name: string;
  description: string;
  type: string;
  scenarios: Array<{
    name: string;
    variables: Record<string, any>;
  }>;
  metrics: string[];
}

const SimulationTemplates: React.FC = () => {
  const [templates, setTemplates] = useState<SimulationTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<TemplateDetail | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [resultsOpen, setResultsOpen] = useState(false);
  const [simulationResults, setSimulationResults] = useState<any>(null);
  const [runningSimulation, setRunningSimulation] = useState(false);
  
  // Edit mode state
  const [editMode, setEditMode] = useState(false);
  const [editedTemplate, setEditedTemplate] = useState<TemplateDetail | null>(null);
  const [saving, setSaving] = useState(false);

  // API base URL - ASCIN runs on port 8005
  const API_BASE_URL = process.env.REACT_APP_API_URL || '';

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/v1/simulation/templates/`);
      if (!response.ok) throw new Error('Failed to fetch templates');
      const data = await response.json();
      setTemplates(data.templates);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplateDetails = async (templateId: string) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/v1/simulation/templates/${templateId}`
      );
      if (!response.ok) throw new Error('Failed to fetch template details');
      const data = await response.json();
      setSelectedTemplate(data);
      setDetailsOpen(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    }
  };

  const handleRunSimulation = async (templateId: string) => {
    try {
      setRunningSimulation(true);
      setError(null);
      const response = await fetch(`${API_BASE_URL}/api/v1/simulation/templates/run`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          template_id: templateId,
          selected_scenarios: [0, 1, 2], // Run all scenarios
          duration_days: 30,
          iterations: 100,
        }),
      });
      if (!response.ok) throw new Error('Failed to run simulation');
      const data = await response.json();
      setSimulationResults(data);
      setResultsOpen(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setRunningSimulation(false);
    }
  };

  // Edit handlers
  const handleEditTemplate = async (templateId: string) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/v1/simulation/templates/${templateId}`
      );
      if (!response.ok) throw new Error('Failed to fetch template details');
      const data = await response.json();
      setEditedTemplate(JSON.parse(JSON.stringify(data))); // Deep copy for editing
      setEditMode(true);
      setDetailsOpen(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    }
  };

  const handleVariableChange = (scenarioIndex: number, variableName: string, value: string) => {
    if (!editedTemplate) return;
    
    const updated = { ...editedTemplate };
    const numValue = parseFloat(value);
    updated.scenarios[scenarioIndex].variables[variableName] = isNaN(numValue) ? value : numValue;
    setEditedTemplate(updated);
  };

  const handleScenarioNameChange = (scenarioIndex: number, name: string) => {
    if (!editedTemplate) return;
    
    const updated = { ...editedTemplate };
    updated.scenarios[scenarioIndex].name = name;
    setEditedTemplate(updated);
  };

  const handleSaveTemplate = async () => {
    if (!editedTemplate) return;
    
    try {
      setSaving(true);
      setError(null);
      
      const response = await fetch(
        `${API_BASE_URL}/api/v1/simulation/templates/${editedTemplate.template_id}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: editedTemplate.name,
            description: editedTemplate.description,
            scenarios: editedTemplate.scenarios,
          }),
        }
      );
      
      if (!response.ok) throw new Error('Failed to save template');
      
      // Refresh templates list
      await fetchTemplates();
      
      // Close edit mode
      setEditMode(false);
      setEditedTemplate(null);
      setDetailsOpen(false);
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setEditMode(false);
    setEditedTemplate(null);
    // If we were viewing, keep the dialog open with original data
    if (selectedTemplate) {
      setDetailsOpen(true);
    } else {
      setDetailsOpen(false);
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'transportation':
        return <ShippingIcon />;
      case 'product_design':
        return <ScienceIcon />;
      case 'demand_surge':
        return <TrendingUpIcon />;
      case 'supplier_disruption':
        return <InventoryIcon />;
      case 'inventory_policy':
        return <InventoryIcon />;
      case 'manufacturing':
        return <FactoryIcon />;
      case 'distribution':
        return <WarehouseIcon />;
      default:
        return <ScienceIcon />;
    }
  };

  const getTypeColor = (type: string): 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'info' => {
    switch (type) {
      case 'transportation':
        return 'primary';
      case 'product_design':
        return 'secondary';
      case 'demand_surge':
        return 'warning';
      case 'supplier_disruption':
        return 'error' as any;
      case 'inventory_policy':
        return 'info';
      case 'manufacturing':
        return 'success';
      case 'distribution':
        return 'primary';
      default:
        return 'default';
    }
  };

  const formatVariableName = (name: string): string => {
    return name
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const formatVariableValue = (value: any): string => {
    if (typeof value === 'number') {
      return value.toLocaleString();
    }
    return String(value);
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          <ScienceIcon sx={{ mr: 2, verticalAlign: 'middle' }} />
          Simulation Templates
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Pre-configured simulation scenarios for common supply chain decisions. Select a
          template to customize variables and run simulations.
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={3}>
          {templates.map((template) => (
            <Grid item xs={12} sm={6} md={4} key={template.id}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  '&:hover': {
                    boxShadow: 6,
                    transform: 'translateY(-4px)',
                    transition: 'all 0.3s',
                  },
                }}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    {getTypeIcon(template.type)}
                    <Typography variant="h6" component="h2" sx={{ ml: 1 }}>
                      {template.name}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {template.description}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Chip
                      label={formatVariableName(template.type)}
                      size="small"
                      color={getTypeColor(template.type)}
                    />
                    <Chip
                      label={`${template.scenario_count} scenarios`}
                      size="small"
                      variant="outlined"
                    />
                  </Box>
                </CardContent>
                <CardActions>
                  <Button
                    size="small"
                    onClick={() => fetchTemplateDetails(template.id)}
                  >
                    View Details
                  </Button>
                  <Button
                    size="small"
                    startIcon={<EditIcon />}
                    color="secondary"
                    onClick={() => handleEditTemplate(template.id)}
                  >
                    Edit
                  </Button>
                  <Button
                    size="small"
                    startIcon={<PlayIcon />}
                    color="primary"
                    variant="contained"
                    onClick={() => handleRunSimulation(template.id)}
                  >
                    Run
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Template Details Dialog - View and Edit Mode */}
      <Dialog
        open={detailsOpen}
        onClose={() => {
          setDetailsOpen(false);
          setEditMode(false);
          setEditedTemplate(null);
        }}
        maxWidth="md"
        fullWidth
      >
        {/* View Mode */}
        {selectedTemplate && !editMode && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {getTypeIcon(selectedTemplate.type)}
                <Typography variant="h6" sx={{ ml: 1 }}>
                  {selectedTemplate.name}
                </Typography>
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {selectedTemplate.description}
              </Typography>

              <Typography variant="h6" gutterBottom>
                Scenarios
              </Typography>
              {selectedTemplate.scenarios.map((scenario, index) => (
                <Accordion key={index}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography>{scenario.name}</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Variable</TableCell>
                            <TableCell align="right">Value</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {Object.entries(scenario.variables).map(([key, value]) => (
                            <TableRow key={key}>
                              <TableCell>{formatVariableName(key)}</TableCell>
                              <TableCell align="right">
                                {formatVariableValue(value)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </AccordionDetails>
                </Accordion>
              ))}

              <Box sx={{ mt: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Comparison Metrics
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {selectedTemplate.metrics.map((metric) => (
                    <Chip
                      key={metric}
                      label={formatVariableName(metric)}
                      variant="outlined"
                      size="small"
                    />
                  ))}
                </Box>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDetailsOpen(false)}>Close</Button>
              <Button
                color="secondary"
                startIcon={<EditIcon />}
                onClick={() => {
                  setEditedTemplate(JSON.parse(JSON.stringify(selectedTemplate)));
                  setEditMode(true);
                }}
              >
                Edit Template
              </Button>
              <Button
                variant="contained"
                startIcon={runningSimulation ? <CircularProgress size={20} /> : <PlayIcon />}
                onClick={() => {
                  handleRunSimulation(selectedTemplate.template_id);
                  setDetailsOpen(false);
                }}
                disabled={runningSimulation}
              >
                {runningSimulation ? 'Running...' : 'Run Simulation'}
              </Button>
            </DialogActions>
          </>
        )}

        {/* Edit Mode */}
        {editedTemplate && editMode && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <EditIcon color="secondary" />
                <Typography variant="h6" sx={{ ml: 1 }}>
                  Edit Template: {editedTemplate.name}
                </Typography>
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              <Alert severity="info" sx={{ mb: 3 }}>
                Modify scenario names and variable values below. Changes will be saved when you click "Save Changes".
              </Alert>

              <Typography variant="h6" gutterBottom>
                Scenarios
              </Typography>
              {editedTemplate.scenarios.map((scenario, scenarioIndex) => (
                <Accordion key={scenarioIndex} defaultExpanded={scenarioIndex === 0}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <TextField
                      value={scenario.name}
                      onChange={(e) => handleScenarioNameChange(scenarioIndex, e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      size="small"
                      variant="outlined"
                      sx={{ width: '100%' }}
                    />
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={2}>
                      {Object.entries(scenario.variables).map(([key, value]) => (
                        <Grid item xs={12} sm={6} key={key}>
                          <TextField
                            fullWidth
                            label={formatVariableName(key)}
                            value={value}
                            onChange={(e) => handleVariableChange(scenarioIndex, key, e.target.value)}
                            size="small"
                            type={typeof value === 'number' ? 'number' : 'text'}
                            InputProps={{
                              endAdornment: typeof value === 'number' && key.includes('percent') ? '%' : undefined,
                            }}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              ))}
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCancelEdit} disabled={saving}>
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSaveTemplate}
                disabled={saving}
                startIcon={saving ? <CircularProgress size={20} /> : undefined}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Simulation Results Dialog */}
      <Dialog
        open={resultsOpen}
        onClose={() => setResultsOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        {simulationResults && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="h6">
                  🎯 Simulation Results: {simulationResults.template_name}
                </Typography>
                <Chip
                  label={`${simulationResults.iterations} iterations`}
                  color="primary"
                  size="small"
                />
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              {/* Recommendation */}
              <Alert severity="success" sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Recommended: {simulationResults.recommendation}
                </Typography>
                <Typography variant="body2">
                  {simulationResults.recommendation_reason}
                </Typography>
              </Alert>

              {/* Results Comparison */}
              <Typography variant="h6" gutterBottom>
                Scenario Comparison
              </Typography>
              {simulationResults.results.map((result: any, index: number) => (
                <Card
                  key={index}
                  sx={{
                    mb: 2,
                    border: result.scenario_name === simulationResults.recommendation ? 2 : 1,
                    borderColor: result.scenario_name === simulationResults.recommendation ? 'success.main' : 'divider',
                  }}
                >
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {result.scenario_name}
                      {result.scenario_name === simulationResults.recommendation && (
                        <Chip label="Best" color="success" size="small" sx={{ ml: 1 }} />
                      )}
                    </Typography>
                    <Grid container spacing={2}>
                      {Object.entries(result.metrics).map(([key, value]: [string, any]) => (
                        <Grid item xs={12} sm={6} md={4} key={key}>
                          <Paper sx={{ p: 2, textAlign: 'center' }} variant="outlined">
                            <Typography variant="body2" color="text.secondary">
                              {formatVariableName(key)}
                            </Typography>
                            <Typography variant="h6">
                              {typeof value === 'number'
                                ? value.toFixed(2)
                                : String(value)}
                            </Typography>
                          </Paper>
                        </Grid>
                      ))}
                    </Grid>
                  </CardContent>
                </Card>
              ))}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setResultsOpen(false)}>Close</Button>
              <Button
                variant="outlined"
                onClick={() => {
                  const dataStr = JSON.stringify(simulationResults, null, 2);
                  const dataBlob = new Blob([dataStr], { type: 'application/json' });
                  const url = URL.createObjectURL(dataBlob);
                  const link = document.createElement('a');
                  link.href = url;
                  link.download = `simulation-results-${Date.now()}.json`;
                  link.click();
                }}
              >
                Export Results
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Container>
  );
};

export default SimulationTemplates;
